import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { ProgressSpinner } from 'primereact/progressspinner';
import axios from 'axios';
import { jsPDF } from 'jspdf';
import { autoTable } from 'jspdf-autotable';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Doughnut, Bar } from 'react-chartjs-2';
import './App.css';

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3006/api';

function ReportDailyAttendance() {
    const [attendanceData, setAttendanceData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [teacherType, setTeacherType] = useState('All');
    const [teacherTypeList, setTeacherTypeList] = useState([]);
    const [statusFilter, setStatusFilter] = useState('1');
    const [totalRecords, setTotalRecords] = useState(0);
    const [devSupportStaff, setDevSupportStaff] = useState([]);
    const [prevWorkingDay, setPrevWorkingDay] = useState('');
    const [lowAttendance, setLowAttendance] = useState([]);
    const donutRef = useRef(null);
    const barRef = useRef(null);

    const statusOptions = [
        { label: 'All', value: '1' },
        { label: 'Present', value: '2' },
        { label: 'Absent', value: '3' }
    ];

    const fetchTeacherTypes = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/attendance/teacher-types`);
            setTeacherTypeList(response.data || []);
        } catch (error) {
            console.error('Error fetching teacher types:', error);
        }
    }, []);

    const fetchDevSupportStaff = useCallback(async (dateStr) => {
        try {
            const response = await axios.get(`${API_BASE_URL}/attendance/dev-support-staff`, {
                params: { date: dateStr }
            });
            setDevSupportStaff(response.data.data || []);
            setPrevWorkingDay(response.data.previousWorkingDay || '');
        } catch (error) {
            console.error('Error fetching dev support staff:', error);
            setDevSupportStaff([]);
        }
    }, []);

    const fetchLowAttendance = useCallback(async () => {
        try {
            const response = await axios.get(`${API_BASE_URL}/dashboard/low-attendance`);
            setLowAttendance(response.data.data || []);
        } catch (error) {
            console.error('Error fetching low attendance:', error);
            setLowAttendance([]);
        }
    }, []);

    const fetchAttendanceData = useCallback(async () => {
        setLoading(true);
        try {
            const dateStr = selectedDate.toISOString().split('T')[0];
            const response = await axios.get(`${API_BASE_URL}/attendance/daily`, {
                params: {
                    date: dateStr,
                    teacherType: teacherType === 'All' ? '' : teacherType,
                    status: statusFilter,
                    first: 0,
                    rows: 50000
                }
            });

            setAttendanceData(response.data.data || []);
            setTotalRecords(response.data.totalRecords || 0);
            fetchDevSupportStaff(dateStr);
        } catch (error) {
            console.error('Error fetching daily attendance:', error);
            setAttendanceData([]);
            setTotalRecords(0);
            const msg = error.response?.data?.message || error.message || 'Unknown error';
            alert(`Failed to load report data. Check that the API server is running.\n\nError: ${msg}`);
        } finally {
            setLoading(false);
        }
    }, [selectedDate, teacherType, statusFilter, fetchDevSupportStaff]);

    useEffect(() => {
        fetchTeacherTypes();
        fetchLowAttendance();
    }, [fetchTeacherTypes, fetchLowAttendance]);

    useEffect(() => {
        fetchAttendanceData();
    }, [fetchAttendanceData]);

    const formattedDate = selectedDate.toLocaleDateString('en-US', {
        weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });

    const getReportTitle = () => `TEACHERS DAILY ATTENDANCE REPORT - ${formattedDate}`;

    const isPresent = (row) => (row.in_time && row.in_time !== '-') || row.is_attended;

    const statusTemplate = (rowData) => {
        const hasAttended = isPresent(rowData);
        const label = hasAttended ? 'Present' : 'Absent';
        const color = hasAttended ? '#28a745' : '#dc3545';
        return <span style={{ color, fontWeight: 'bold' }}>{label}</span>;
    };

    const timeTemplate = (field) => (rowData) => {
        const val = rowData[field];
        if (!val) return <span style={{ color: '#ccc' }}>-</span>;
        if (typeof val === 'string' && val.includes(':')) return val;
        const d = new Date(val);
        return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', hour12: true });
    };

    const presentCount = attendanceData.filter(r => isPresent(r)).length;
    const absentCount = attendanceData.length - presentCount;
    const attPct = attendanceData.length ? ((presentCount / attendanceData.length) * 100).toFixed(1) : '0.0';

    const typeStats = useMemo(() => {
        const map = {};
        attendanceData.forEach(row => {
            const type = row.grades || 'Unknown';
            if (!map[type]) map[type] = { present: 0, absent: 0 };
            if (isPresent(row)) map[type].present++;
            else map[type].absent++;
        });
        return map;
    }, [attendanceData]);

    const arrivalStats = useMemo(() => {
        const buckets = { 'Before 7:30': 0, '7:30-8:00': 0, '8:00-8:30': 0, 'After 8:30': 0, 'No Time': 0 };
        attendanceData.forEach(row => {
            if (!isPresent(row)) return;
            const t = row.in_time;
            if (!t || typeof t !== 'string' || !t.includes(':')) { buckets['No Time']++; return; }
            const parts = t.split(':');
            const mins = parseInt(parts[0]) * 60 + parseInt(parts[1]);
            if (mins < 450) buckets['Before 7:30']++;
            else if (mins < 480) buckets['7:30-8:00']++;
            else if (mins < 510) buckets['8:00-8:30']++;
            else buckets['After 8:30']++;
        });
        return buckets;
    }, [attendanceData]);

    const donutData = useMemo(() => ({
        labels: ['Present', 'Absent'],
        datasets: [{
            data: [presentCount, absentCount],
            backgroundColor: ['#28a745', '#dc3545'],
            borderWidth: 2, borderColor: '#fff'
        }]
    }), [attendanceData, presentCount, absentCount]);

    const donutOptions = {
        responsive: true, maintainAspectRatio: false,
        cutout: '60%',
        plugins: {
            legend: { position: 'bottom' },
            tooltip: { callbacks: { label: (ctx) => `${ctx.label}: ${ctx.raw} (${attendanceData.length ? ((ctx.raw / attendanceData.length) * 100).toFixed(1) : 0}%)` } }
        }
    };

    const barData = useMemo(() => {
        const types = Object.keys(typeStats);
        return {
            labels: types,
            datasets: [
                { label: 'Present', data: types.map(t => typeStats[t].present), backgroundColor: '#28a745' },
                { label: 'Absent', data: types.map(t => typeStats[t].absent), backgroundColor: '#dc3545' }
            ]
        };
    }, [typeStats]);

    const barOptions = {
        responsive: true, maintainAspectRatio: false,
        indexAxis: 'y',
        scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, grid: { display: false } } },
        plugins: { legend: { position: 'top' }, title: { display: true, text: 'Attendance by Teacher Type', font: { size: 14 } } }
    };

    const exportToCSV = () => {
        if (attendanceData.length === 0) return alert('No data to export');

        const headers = ['#', 'TID', 'Name', 'Type', 'In Time', 'Out Time', 'Status'];
        const csvRows = [headers.join(',')];
        attendanceData.forEach((row, idx) => {
            csvRows.push([
                idx + 1,
                `"${(row.tid || '').toString().replace(/"/g, '""')}"`,
                `"${(row.teacher_name || '').replace(/"/g, '""')}"`,
                `"${(row.grades || '').replace(/"/g, '""')}"`,
                row.in_time || '-',
                row.out_time || '-',
                isPresent(row) ? 'Present' : 'Absent'
            ].join(','));
        });

        const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        const dateStr = selectedDate.toISOString().split('T')[0];
        link.download = `Daily_Attendance_${dateStr}.csv`;
        link.click();
        URL.revokeObjectURL(link.href);
    };

    const exportToPDF = () => {
        if (attendanceData.length === 0) return alert('No data to export');

        const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
        const pageW = doc.internal.pageSize.getWidth();

        // Page 1: Title + KPI + Charts
        doc.setFontSize(16);
        doc.setTextColor(30, 58, 95);
        doc.text(getReportTitle(), pageW / 2, 18, { align: 'center' });

        // KPI boxes
        const kpiY = 28;
        const kpiW = 42;
        const kpiH = 22;
        const kpiGap = 5;
        const kpiStartX = (pageW - (4 * kpiW + 3 * kpiGap)) / 2;

        const kpis = [
            { label: 'Total Teachers', value: attendanceData.length, color: [30, 58, 95] },
            { label: 'Present', value: presentCount, color: [40, 167, 69] },
            { label: 'Absent', value: absentCount, color: [220, 53, 69] },
            { label: 'Attendance %', value: `${attPct}%`, color: [0, 123, 255] }
        ];

        kpis.forEach((kpi, i) => {
            const x = kpiStartX + i * (kpiW + kpiGap);
            doc.setFillColor(...kpi.color);
            doc.roundedRect(x, kpiY, kpiW, kpiH, 3, 3, 'F');
            doc.setTextColor(255, 255, 255);
            doc.setFontSize(18);
            doc.text(String(kpi.value), x + kpiW / 2, kpiY + 11, { align: 'center' });
            doc.setFontSize(8);
            doc.text(kpi.label, x + kpiW / 2, kpiY + 18, { align: 'center' });
        });

        // Charts as images
        const chartY = 58;
        if (donutRef.current) {
            const donutImg = donutRef.current.toBase64Image();
            doc.addImage(donutImg, 'PNG', 14, chartY, 80, 80);
        }
        if (barRef.current) {
            const barImg = barRef.current.toBase64Image();
            doc.addImage(barImg, 'PNG', 105, chartY, 90, 80);
        }

        // Arrival time summary below charts
        const summaryY = chartY + 88;
        doc.setTextColor(30, 58, 95);
        doc.setFontSize(11);
        doc.text('Arrival Time Distribution', 14, summaryY);
        const arrLabels = Object.keys(arrivalStats);
        const arrValues = Object.values(arrivalStats);
        autoTable(doc, {
            head: [arrLabels],
            body: [arrValues],
            startY: summaryY + 3,
            theme: 'grid',
            styles: { fontSize: 9, cellPadding: 3, halign: 'center', lineColor: [200, 200, 200], lineWidth: 0.1 },
            headStyles: { fillColor: [30, 58, 95], lineColor: [200, 200, 200], lineWidth: 0.1 }
        });

        // Development Support Staff section on page 1 (if data exists)
        if (devSupportStaff.length > 0) {
            const dssY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : summaryY + 25;
            doc.setTextColor(30, 58, 95);
            doc.setFontSize(11);
            doc.text(`Development Support Staff — Left After 3:30 PM (${devSupportStaff.length} teachers)`, 14, dssY);
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text(`Data from previous working day: ${prevWorkingDay}`, 14, dssY + 5);

            const dssHeaders = ['#', 'TID', 'Name', 'Type', `In Time (${prevWorkingDay})`, `Out Time (${prevWorkingDay})`];
            const dssBody = devSupportStaff.map((row, idx) => [
                idx + 1,
                row.tid,
                row.teacher_name,
                row.grades || '-',
                row.in_time || '-',
                row.out_time || '-'
            ]);

            autoTable(doc, {
                head: [dssHeaders],
                body: dssBody,
                startY: dssY + 8,
                theme: 'grid',
                styles: { fontSize: 8, cellPadding: 2, lineColor: [200, 200, 200], lineWidth: 0.1 },
                headStyles: { fillColor: [139, 69, 19], lineColor: [200, 200, 200], lineWidth: 0.1 },
                columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 13 }, 2: { cellWidth: 45 } }
            });
        }

        // Teachers Needing Attention section
        if (lowAttendance.length > 0) {
            const lowY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 8 : summaryY + 25;
            doc.setTextColor(217, 119, 6);
            doc.setFontSize(11);
            doc.text(`Teachers Needing Attention (${lowAttendance.length}) — Last 30 Days`, 14, lowY);
            doc.setFontSize(8);
            doc.setTextColor(100, 100, 100);
            doc.text('Teachers with less than 50% attendance in the last 30 days', 14, lowY + 4);

            autoTable(doc, {
                head: [['#', 'TID', 'Name', 'Type', 'Days Present', 'Rate']],
                body: lowAttendance.map((row, idx) => [
                    idx + 1, row.tid, row.name, row.teacherType,
                    `${row.daysPresent} / 30`, `${row.attendanceRate}%`
                ]),
                startY: lowY + 7,
                theme: 'grid',
                styles: { fontSize: 8, cellPadding: 2, lineColor: [200, 200, 200], lineWidth: 0.1 },
                headStyles: { fillColor: [245, 158, 11], lineColor: [200, 200, 200], lineWidth: 0.1 },
                columnStyles: { 0: { cellWidth: 8 }, 1: { cellWidth: 13 }, 2: { cellWidth: 45 }, 4: { halign: 'center' }, 5: { halign: 'center' } }
            });
        }

        // Page 2+: Present Teachers
        const presentData = attendanceData.filter(r => isPresent(r));
        const absentData = attendanceData.filter(r => !isPresent(r));

        doc.addPage();
        doc.setFontSize(12);
        doc.setTextColor(40, 167, 69);
        doc.text(`Present Teachers (${presentData.length})`, 14, 15);

        autoTable(doc, {
            head: [['#', 'TID', 'Name', 'Type', 'In Time', 'Out Time']],
            body: presentData.map((row, idx) => [
                idx + 1, row.tid, row.teacher_name,
                row.grades || '', row.in_time || '-', row.out_time || '-'
            ]),
            startY: 20,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 2, lineColor: [200, 200, 200], lineWidth: 0.1 },
            headStyles: { fillColor: [40, 167, 69], lineColor: [200, 200, 200], lineWidth: 0.1 },
            columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 15 }, 2: { cellWidth: 50 } }
        });

        // Absent Teachers (continues after present table, new page if needed)
        const absentStartY = doc.lastAutoTable ? doc.lastAutoTable.finalY + 10 : 20;
        doc.setFontSize(12);
        doc.setTextColor(220, 53, 69);
        doc.text(`Absent Teachers (${absentData.length})`, 14, absentStartY);

        autoTable(doc, {
            head: [['#', 'TID', 'Name', 'Type']],
            body: absentData.map((row, idx) => [
                idx + 1, row.tid, row.teacher_name, row.grades || ''
            ]),
            startY: absentStartY + 5,
            theme: 'grid',
            styles: { fontSize: 8, cellPadding: 2, lineColor: [200, 200, 200], lineWidth: 0.1 },
            headStyles: { fillColor: [220, 53, 69], lineColor: [200, 200, 200], lineWidth: 0.1 },
            columnStyles: { 0: { cellWidth: 10 }, 1: { cellWidth: 15 }, 2: { cellWidth: 50 } }
        });

        const dateStr = selectedDate.toISOString().split('T')[0];
        doc.save(`Daily_Attendance_${dateStr}.pdf`);
    };

    const teacherTypeOptions = teacherTypeList.length > 0
        ? teacherTypeList.map(t => ({ label: t.label || t.type || t, value: t.value || t.type || t }))
        : [{ label: 'All', value: 'All' }];

    const kpiCardStyle = (bg) => ({
        flex: '1 1 150px', background: bg, color: '#fff', borderRadius: '10px',
        padding: '1rem', textAlign: 'center', minWidth: '140px'
    });

    return (
        <div style={{ padding: '1.5rem' }}>
            <h2 style={{ textAlign: 'center', color: '#1e3a5f', marginBottom: '1rem' }}>
                {getReportTitle()}
            </h2>

            <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap' }}>
                <div>
                    <label style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>Select Date</label>
                    <Calendar value={selectedDate} onChange={(e) => setSelectedDate(e.value)} dateFormat="yy-mm-dd" showIcon />
                </div>
                <div>
                    <label style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>Teacher Type</label>
                    <Dropdown value={teacherType} options={teacherTypeOptions} onChange={(e) => setTeacherType(e.value)} style={{ minWidth: '150px' }} />
                </div>
                <div>
                    <label style={{ fontWeight: 'bold', marginRight: '0.5rem' }}>Status</label>
                    <Dropdown value={statusFilter} options={statusOptions} onChange={(e) => setStatusFilter(e.value)} style={{ minWidth: '120px' }} />
                </div>
                <Button label="Search" icon="pi pi-search" onClick={fetchAttendanceData} className="p-button-primary" />
                <div style={{ marginLeft: 'auto', display: 'flex', gap: '0.5rem' }}>
                    <Button label="CSV" icon="pi pi-file" onClick={exportToCSV} className="p-button-info" />
                    <Button label="PDF" icon="pi pi-file-pdf" onClick={exportToPDF} className="p-button-danger" />
                </div>
            </div>

            {/* KPI Cards */}
            <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                <div style={kpiCardStyle('#1e3a5f')}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{attendanceData.length}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Total Teachers</div>
                </div>
                <div style={kpiCardStyle('#28a745')}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{presentCount}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Present</div>
                </div>
                <div style={kpiCardStyle('#dc3545')}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{absentCount}</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Absent</div>
                </div>
                <div style={kpiCardStyle('#007bff')}>
                    <div style={{ fontSize: '2rem', fontWeight: 'bold' }}>{attPct}%</div>
                    <div style={{ fontSize: '0.85rem', opacity: 0.9 }}>Attendance Rate</div>
                </div>
            </div>

            {/* Charts Row */}
            {!loading && attendanceData.length > 0 && (
                <div style={{ display: 'flex', gap: '1.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                    <div style={{ flex: '1 1 280px', maxWidth: '350px', background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '1rem' }}>
                        <h4 style={{ textAlign: 'center', color: '#1e3a5f', marginTop: 0 }}>Present vs Absent</h4>
                        <div style={{ height: '250px' }}>
                            <Doughnut ref={donutRef} data={donutData} options={donutOptions} />
                        </div>
                    </div>
                    <div style={{ flex: '2 1 400px', background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '1rem' }}>
                        <div style={{ height: '280px' }}>
                            <Bar ref={barRef} data={barData} options={barOptions} />
                        </div>
                    </div>
                </div>
            )}

            {/* Development Support Staff Section */}
            {!loading && devSupportStaff.length > 0 && (
                <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '1rem', marginBottom: '1.5rem', borderLeft: '4px solid #8B4513' }}>
                    <h3 style={{ color: '#8B4513', marginTop: 0, marginBottom: '0.25rem' }}>
                        Development Support Staff — Left After 3:30 PM ({devSupportStaff.length} teachers)
                    </h3>
                    <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 0.75rem 0' }}>
                        Data from previous working day: <strong>{prevWorkingDay}</strong>
                    </p>
                    <DataTable value={devSupportStaff} size="small" stripedRows
                        emptyMessage="No teachers left after 3:30 PM on previous working day.">
                        <Column header="#" body={(data, options) => options.rowIndex + 1} style={{ width: '40px', textAlign: 'center' }} />
                        <Column field="tid" header="TID" style={{ width: '60px' }} />
                        <Column field="teacher_name" header="Name" style={{ minWidth: '200px' }} />
                        <Column field="grades" header="Type" style={{ width: '100px' }} />
                        <Column field="in_time" header={`In Time (${prevWorkingDay})`} style={{ width: '130px' }} />
                        <Column field="out_time" header={`Out Time (${prevWorkingDay})`} style={{ width: '140px' }}
                            body={(row) => <span style={{ color: '#28a745', fontWeight: 'bold' }}>{row.out_time}</span>} />
                    </DataTable>
                </div>
            )}

            {/* Teachers Needing Attention — Last 30 Days */}
            {!loading && lowAttendance.length > 0 && (
                <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '1rem', marginBottom: '1.5rem', borderLeft: '4px solid #F59E0B' }}>
                    <h3 style={{ color: '#D97706', marginTop: 0, marginBottom: '0.25rem' }}>
                        Teachers Needing Attention ({lowAttendance.length})
                    </h3>
                    <p style={{ color: '#666', fontSize: '0.85rem', margin: '0 0 0.75rem 0' }}>
                        Teachers with less than 50% attendance in the last 30 days
                    </p>
                    <DataTable value={lowAttendance} size="small" stripedRows
                        emptyMessage="All teachers have good attendance!">
                        <Column header="#" body={(data, options) => options.rowIndex + 1} style={{ width: '40px', textAlign: 'center' }} />
                        <Column field="tid" header="TID" style={{ width: '60px' }} />
                        <Column field="name" header="Name" style={{ minWidth: '200px' }} />
                        <Column field="teacherType" header="Type" style={{ width: '100px' }} />
                        <Column field="daysPresent" header="Days Present (30d)" style={{ width: '130px', textAlign: 'center' }}
                            body={(row) => <span style={{ fontWeight: 'bold', color: row.daysPresent < 5 ? '#dc3545' : '#D97706' }}>{row.daysPresent} / 30</span>} />
                        <Column field="attendanceRate" header="Rate" style={{ width: '80px', textAlign: 'center' }}
                            body={(row) => <span style={{
                                fontWeight: 'bold', color: '#fff', padding: '2px 8px', borderRadius: '4px',
                                background: row.attendanceRate < 25 ? '#dc3545' : '#F59E0B'
                            }}>{row.attendanceRate}%</span>} />
                    </DataTable>
                </div>
            )}

            {/* Data Tables: Present + Absent */}
            {loading ? (
                <div style={{ textAlign: 'center', padding: '3rem' }}><ProgressSpinner /></div>
            ) : (
                <>
                    {/* Present Teachers */}
                    <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '1rem', marginBottom: '1.5rem', borderLeft: '4px solid #28a745' }}>
                        <h3 style={{ color: '#28a745', marginTop: 0, marginBottom: '0.5rem' }}>
                            Present Teachers ({presentCount})
                        </h3>
                        <DataTable value={attendanceData.filter(r => isPresent(r))} scrollable scrollHeight="400px"
                            emptyMessage="No present teachers found." size="small" stripedRows>
                            <Column header="#" body={(data, options) => options.rowIndex + 1} style={{ width: '50px', textAlign: 'center' }} />
                            <Column field="tid" header="TID" style={{ width: '70px' }} />
                            <Column field="teacher_name" header="Name" style={{ minWidth: '200px' }} />
                            <Column field="grades" header="Type" style={{ width: '120px' }} />
                            <Column field="in_time" header="In Time" style={{ width: '100px' }} body={timeTemplate('in_time')} />
                            <Column field="out_time" header="Out Time" style={{ width: '100px' }} body={timeTemplate('out_time')} />
                        </DataTable>
                    </div>

                    {/* Absent Teachers */}
                    <div style={{ background: '#fff', borderRadius: '10px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '1rem', marginBottom: '1.5rem', borderLeft: '4px solid #dc3545' }}>
                        <h3 style={{ color: '#dc3545', marginTop: 0, marginBottom: '0.5rem' }}>
                            Absent Teachers ({absentCount})
                        </h3>
                        <DataTable value={attendanceData.filter(r => !isPresent(r))} scrollable scrollHeight="400px"
                            emptyMessage="No absent teachers found." size="small" stripedRows>
                            <Column header="#" body={(data, options) => options.rowIndex + 1} style={{ width: '50px', textAlign: 'center' }} />
                            <Column field="tid" header="TID" style={{ width: '70px' }} />
                            <Column field="teacher_name" header="Name" style={{ minWidth: '200px' }} />
                            <Column field="grades" header="Type" style={{ width: '120px' }} />
                        </DataTable>
                    </div>
                </>
            )}
        </div>
    );
}

export default ReportDailyAttendance;
